import PropTypes from 'prop-types';
import qs from 'query-string';
import { Component } from 'react';
import { connect } from 'react-redux';

import * as CommonAuthService from '../../../../common/services/auth';
import { RenderPropWithProps, RouteProps, TranslationFuncProp } from '../../../../common/types/prop-types/common';
import { AppThunkDispatch } from '../../../../prime/common/types';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';

import { bindActionCreators } from 'redux';
import { AuthError } from '../../../../common/services/auth/Types';
import * as Actions from '../../../../common/services/customer/Actions';
import * as CustomerService from '../../../../common/services/customer/Service';
import { CustomerActionPropTypes } from '../../../../common/services/customer/Types';

import { jwtVerify } from 'jose';

export interface OAuthCallbackProps {
    isAuthenticating: boolean;
    isAuthenticated: boolean;
    errorCode: string | null;
    errorMessage: string | null;
}

export interface OAuthCallbackContainerProps {

}

interface ReduxStateProps {
    isAuthenticating: boolean;
    isAuthenticated: boolean;
    errorCode: string | null;
    errorMessage: string | null;
}

interface ReduxDispatchProps {
    authServiceActions: CommonAuthService.Service.ServiceActions;
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
}

type OAuthCallBackContainerAllProps = OAuthCallbackContainerProps &
    TranslationFuncProp &
    RouteProps &
    RenderPropWithProps<OAuthCallbackProps> &
    ReduxStateProps &
    ReduxDispatchProps;

class OAuthCallbackContainer extends Component<OAuthCallBackContainerAllProps> {
    public static propTypes = {
        t: PropTypes.func.isRequired,
        customerActions: CustomerActionPropTypes.isRequired,
        customerServiceActions: CustomerService.ServiceActionPropTypes.isRequired,
        routeProps: PropTypes.any.isRequired,
        isAuthenticated: PropTypes.bool.isRequired,
        isAuthenticating: PropTypes.bool.isRequired,
        errorCode: PropTypes.string,
        errorMessage: PropTypes.string,
        location: PropTypes.object,
        authServiceActions: CommonAuthService.Service.ServiceActionPropTypes,
        //
        children: PropTypes.func.isRequired,
    };

    public async componentDidMount() {
        if (this.props.isAuthenticated !== true) {
            const parsed = qs.parse(this.props.routeProps.location.search);
            // let's check the query string params and see if we can login the user
            if (parsed.code && parsed.state) {
                const code: string = Array.isArray(parsed.code) ? (parsed.code[0] || '') : (parsed.code || '');
                const state: string = Array.isArray(parsed.state) ? (parsed.state[0] || '') : (parsed.state || '');
                try {
                    await this.props.authServiceActions.loginUser(code, state);
                } catch (error) {
                    if (error instanceof AuthError && error.authErrorInfo.exceptionType) {
                        const exceptionType: string | undefined = error.authErrorInfo.exceptionType.split('.').pop();
                        if (exceptionType === 'AccountUnconfirmedException') {
                            if (config.IFrameRedirectParent) {
                                window.location.assign(`${config.IFrameRedirectParent}/prime/unconfirmed`);
                            } else {
                                this.props.routeProps.history.push(`${config.BaseURL}/unconfirmed`, { gcid: error.authErrorInfo.exceptionData });
                            }
                        } else {
                            if (config.IFrameRedirectParent) {
                                window.location.assign(`${config.IFrameRedirectParent}/prime/autherror`);
                            } else {
                                this.props.routeProps.history.push(`${config.BaseURL}/autherror`, { gcid: error.authErrorInfo.exceptionData });
                            }
                        }
                    }
                }
            }
        }
    }

    public componentDidUpdate(prevProps: OAuthCallBackContainerAllProps) {
        if (this.props.isAuthenticated && this.props.isAuthenticated !== prevProps.isAuthenticated) {
            const parsed = qs.parse(this.props.routeProps.location.search);
            const state: string = Array.isArray(parsed.state) ? (parsed.state[0] || '') : (parsed.state || '');
            try {
                if (state === 'DEV_MODE_STATE') {
                    this.props.routeProps.history.push(`${config.BaseURL}`);
                } else {
                    const handleVerify = async () => {
                        try {
                            const secret = new TextEncoder().encode(config.JwtSecret);
                            const { payload: jwtDecoded } = await jwtVerify(state, secret);
                            if (jwtDecoded.redirect && typeof jwtDecoded.redirect === 'string' && jwtDecoded.redirect.indexOf('/auth/gcdm/') > -1) {
                                // check for parameters
                                if (jwtDecoded.params && Object.keys(jwtDecoded.params).length > 0) {
                                    // merge querystring (from redirect url and parameters)
                                    const redirectUri = qs.parseUrl(jwtDecoded.redirect);
                                    const finalQs = { ...redirectUri.query, ...jwtDecoded.params };

                                    // Re-assigning to a local variable since payload is usually read-only/const
                                    const finalRedirect = redirectUri.url + (Object.keys(finalQs).length > 0 ? ('?' + qs.stringify(finalQs)) : '');
                                    this.props.routeProps.history.push(finalRedirect);
                                } else {
                                    this.props.routeProps.history.push(jwtDecoded.redirect);
                                }
                            } else if (config.IFrameRedirectParent) {
                                window.location.assign(`${config.IFrameRedirectParent}` + (jwtDecoded.redirect || `${config.BaseURL}`));
                            } else {
                                this.props.routeProps.history.push(jwtDecoded.redirect || `${config.BaseURL}`);
                            }
                        } catch (handleError) {
                            this.props.authServiceActions.logout().then(() => {
                                this.props.routeProps.history.push(`${config.BaseURL}`);
                            });
                        }
                    };
                    handleVerify();
                }
            } catch (error) {
                this.props.authServiceActions.logout().then(() => {
                    this.props.routeProps.history.push(`${config.BaseURL}`);
                });
            }
        }
    }

    public render() {
        const newProps: OAuthCallbackProps = {
            isAuthenticating: this.props.isAuthenticating,
            isAuthenticated: this.props.isAuthenticated,
            errorCode: this.props.errorCode,
            errorMessage: this.props.errorMessage,
        };
        return this.props.children(newProps);
    }
}

const mapStateToProps = (store: RootState) => ({
    isAuthenticating: store.authState.isAuthenticating,
    isAuthenticated: store.authState.isAuthenticated,
    errorCode: store.authState.errorCode,
    errorMessage: store.authState.errorMessage,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    authServiceActions: CommonAuthService.Service.bindServiceActionCreators(config, dispatch),
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OAuthCallbackContainer);
